<!-- 汽运入库管理-历史数据回溯 -->
<template>
  <div class="historical-review-page nx-list-page">
    <el-card shadow="never" class="header-card">
      <div class="page-head">
        <div>
          <h2>汽运入库管理-历史数据回溯</h2>
        </div>
        <TopForm ref="topForm" @search="handleSearch" />
      </div>
    </el-card>

    <matrix-statistics
      span="8"
      :list="infoCardData"
      :gutter="14"
      height="118px"
      v-loading="summaryLoading"
    />

    <el-row :gutter="14" class="content-row">
      <el-col :span="15">
        <el-card shadow="never" class="panel-card">
          <div class="panel-head">
            <span class="panel-title">库存月度趋势</span>
          </div>
          <div ref="historicalChart" v-loading="chartLoading" class="chart"></div>
        </el-card>
      </el-col>
      <el-col :span="9">
        <el-card shadow="never" class="panel-card">
          <div class="panel-head">
            <span class="panel-title">区间变化摘要</span>
            <span class="panel-hint">账面库存</span>
          </div>
          <el-table
            :data="summaryRows"
            border
            height="330"
            class="summary-table"
            :header-cell-style="{ background: '#f3f6fb', color: '#334155' }"
          >
            <el-table-column prop="label" label="指标" min-width="110" />
            <el-table-column prop="value" label="数值" min-width="120" />
            <el-table-column prop="remark" label="说明" min-width="150" show-overflow-tooltip />
          </el-table>
        </el-card>
      </el-col>
    </el-row>

    <el-card shadow="never" class="panel-card detail-card">
      <div class="panel-head">
        <span class="panel-title">历史库存明细</span>
      </div>
      <avue-crud
        ref="crud"
        :option="option"
        :data="tableData"
        :page.sync="page"
        :table-loading="tableLoading"
        @current-change="currentChange"
        @size-change="sizeChange"
        @refresh-change="loadTable"
      />
    </el-card>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import TopForm from './topForm.vue'
import {
  stockSummaryByGroupMonth,
  queryStockWeight,
  stockSummaryDetailPage
} from './api'
import matrixStatistics from '@/components/matrix/matrix-statistics'
import { getSettingDate } from '@/util/date'

function buildDefaultQuery() {
  return {
    startTime: getSettingDate('daySLast7', { format: 'YYYY-MM-DD' }),
    endTime: getSettingDate('dayE', { format: 'YYYY-MM-DD' })
  }
}

function valueOrZero(value) {
  return Number(value || 0)
}

function formatTon(value) {
  return `${valueOrZero(value).toLocaleString('zh-CN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  })} 吨`
}

export default {
  name: 'car-storage-historical-review',
  components: {
    matrixStatistics,
    TopForm
  },
  data() {
    return {
      query: buildDefaultQuery(),
      chart: null,
      resizeHandler: null,
      summaryLoading: false,
      chartLoading: false,
      tableLoading: false,
      infoCardData: [
        { label: '选定期间总入库量', value: 0, unit: ' 吨', key: 'inWeight' },
        { label: '选定期间总出库量', value: 0, unit: ' 吨', key: 'outWeight' },
        { label: '选定期间库存总量', value: 0, unit: ' 吨', key: 'currentWeight' }
      ],
      summaryRows: [
        { label: '入库量', value: '0 吨', remark: '选定区间合计' },
        { label: '出库量', value: '0 吨', remark: '选定区间合计' },
        { label: '净变化', value: '0 吨', remark: '入库量 - 出库量' }
      ],
      tableData: [],
      page: {
        pageSize: 10,
        currentPage: 1,
        total: 0
      },
      option: {
        header: false,
        menu: false,
        addBtn: false,
        editBtn: false,
        delBtn: false,
        selection: false,
        tip: false,
        border: true,
        stripe: true,
        searchShow: false,
        searchBtn: false,
        emptyBtn: false,
        align: 'center',
        column: [
          {
            label: '日期',
            prop: 'dt',
            minWidth: 150,
            formatter: (row) => this.formatDate(row.dt)
          },
          {
            label: '仓库名称',
            prop: 'locationName',
            minWidth: 160,
            overHidden: true
          },
          {
            label: '物资名称',
            prop: 'cargoName',
            minWidth: 140,
            overHidden: true
          },
          {
            label: '入库量(吨)',
            prop: 'inWeight',
            minWidth: 130,
            formatter: (row) => this.formatWeight(row.inWeight)
          },
          {
            label: '出库量(吨)',
            prop: 'outWeight',
            minWidth: 130,
            formatter: (row) => this.formatWeight(row.outWeight)
          },
          {
            label: '库存量(吨)',
            prop: 'currentWeight',
            minWidth: 140,
            formatter: (row) => this.formatWeight(row.currentWeight)
          }
        ]
      }
    }
  },
  mounted() {
    this.resizeHandler = () => {
      if (this.chart) {
        this.chart.resize()
      }
    }
    window.addEventListener('resize', this.resizeHandler)
    this.loadAll()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeHandler)
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
  },
  methods: {
    handleSearch(payload) {
      this.query = {
        startTime: payload.startTime,
        endTime: payload.endTime
      }
      this.page.currentPage = 1
      this.loadAll()
    },
    loadAll() {
      this.loadSummary()
      this.loadTrend()
      this.loadTable()
    },
    async loadSummary() {
      this.summaryLoading = true
      try {
        const { data } = await queryStockWeight(this.query)
        const summary = (data && data.data) || {}
        this.infoCardData.forEach((item) => {
          this.$set(item, 'value', valueOrZero(summary[item.key]))
        })
        const inWeight = valueOrZero(summary.inWeight)
        const outWeight = valueOrZero(summary.outWeight)
        this.summaryRows = [
          { label: '入库量', value: formatTon(inWeight), remark: '选定区间合计' },
          { label: '出库量', value: formatTon(outWeight), remark: '选定区间合计' },
          { label: '净变化', value: formatTon(inWeight - outWeight), remark: '入库量 - 出库量' }
        ]
      } catch (e) {
        this.infoCardData.forEach((item) => {
          this.$set(item, 'value', 0)
        })
        this.$message.error('库存汇总加载失败')
      } finally {
        this.summaryLoading = false
      }
    },
    async loadTrend() {
      this.chartLoading = true
      try {
        const { data } = await stockSummaryByGroupMonth(this.query)
        const chartData = (data && data.data) || {}
        this.renderChart(chartData.title || [], chartData.data || [])
      } catch (e) {
        this.renderChart([], [])
        this.$message.error('库存趋势加载失败')
      } finally {
        this.chartLoading = false
      }
    },
    async loadTable() {
      this.tableLoading = true
      try {
        const { data } = await stockSummaryDetailPage({
          ...this.query,
          page: this.page.currentPage,
          size: this.page.pageSize
        })
        const pageData = (data && data.data) || {}
        this.tableData = (pageData.records || []).map((item) => ({
          ...item,
          inWeight: valueOrZero(item.inWeight),
          outWeight: valueOrZero(item.outWeight),
          currentWeight: valueOrZero(item.currentWeight)
        }))
        this.page.total = pageData.total || 0
      } catch (e) {
        this.tableData = []
        this.page.total = 0
        this.$message.error('历史库存明细加载失败')
      } finally {
        this.tableLoading = false
      }
    },
    currentChange(currentPage) {
      this.page.currentPage = currentPage
      this.loadTable()
    },
    sizeChange(pageSize) {
      this.page.pageSize = pageSize
      this.page.currentPage = 1
      this.loadTable()
    },
    renderChart(title, data) {
      this.$nextTick(() => {
        if (!this.$refs.historicalChart) return
        if (!this.chart) {
          this.chart = echarts.init(this.$refs.historicalChart)
        }
        const xData = title.length ? title.map((item) => `${item}月`) : ['暂无数据']
        const seriesData = data.length ? data.map((item) => valueOrZero(item)) : [0]
        this.chart.setOption({
          color: ['#2563eb'],
          tooltip: {
            trigger: 'axis',
            backgroundColor: 'rgba(15,23,42,.92)',
            borderWidth: 0,
            padding: [10, 12],
            textStyle: {
              color: '#fff',
              fontSize: 12
            },
            axisPointer: {
              type: 'line',
              lineStyle: {
                color: '#93c5fd',
                width: 1,
                type: 'dashed'
              }
            },
            valueFormatter: (value) => formatTon(value)
          },
          grid: {
            left: 32,
            right: 28,
            top: 34,
            bottom: 34,
            containLabel: true
          },
          xAxis: {
            type: 'category',
            boundaryGap: false,
            data: xData,
            axisTick: { show: false },
            axisLine: {
              lineStyle: { color: '#dbe3ef' }
            },
            axisLabel: {
              color: '#64748b',
              margin: 12
            }
          },
          yAxis: {
            type: 'value',
            name: '吨',
            nameTextStyle: {
              color: '#94a3b8',
              padding: [0, 28, 0, 0]
            },
            axisTick: { show: false },
            axisLine: { show: false },
            axisLabel: {
              color: '#64748b'
            },
            splitLine: {
              lineStyle: {
                color: '#e8edf5',
                type: 'dashed'
              }
            }
          },
          series: [{
            name: '库存量',
            data: seriesData,
            type: 'line',
            smooth: true,
            symbol: 'circle',
            showSymbol: false,
            symbolSize: 8,
            emphasis: {
              focus: 'series'
            },
            lineStyle: {
              color: '#2563eb',
              width: 3,
              shadowColor: 'rgba(37,99,235,.2)',
              shadowBlur: 8,
              shadowOffsetY: 4
            },
            itemStyle: {
              color: '#2563eb',
              borderColor: '#fff',
              borderWidth: 2
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 1, 0, 0, [
                { offset: 0, color: 'rgba(37,99,235,0)' },
                { offset: .45, color: 'rgba(37,99,235,.08)' },
                { offset: 1, color: 'rgba(37,99,235,.24)' }
              ])
            }
          }]
        }, true)
        this.chart.resize()
      })
    },
    formatDate(value) {
      if (!value) return '-'
      return String(value).slice(0, 10)
    },
    formatWeight(value) {
      return valueOrZero(value).toLocaleString('zh-CN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      })
    }
  }
}
</script>

<style scoped lang="scss">
.historical-review-page {
  min-width: 1180px;
  padding: 12px;
  background: #f5f7fb;
}

.header-card,
.panel-card {
  border: 1px solid #dde6f3;
  border-radius: 8px;
  box-shadow: 0 8px 22px rgba(15, 23, 42, .04);
}

.header-card {
  margin-bottom: 14px;

  ::v-deep .el-card__body {
    padding: 18px 20px;
  }
}

.page-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 44px;

  h2 {
    position: relative;
    margin: 0;
    padding-left: 12px;
    color: #172033;
    font-size: 20px;
    font-weight: 700;
    line-height: 28px;

    &::before {
      position: absolute;
      top: 4px;
      left: 0;
      width: 4px;
      height: 20px;
      content: '';
      background: #2563eb;
      border-radius: 2px;
    }
  }

  p {
    margin: 6px 0 0;
    color: #6b7280;
    font-size: 12px;
  }
}

.content-row {
  margin-bottom: 14px;
}

.historical-review-page ::v-deep .statistics {
  margin-bottom: 14px;
}

.historical-review-page ::v-deep .statistics .list .box-card {
  position: relative;
  overflow: hidden;
  border: 1px solid #dde6f3;
  border-radius: 8px;
  box-shadow: 0 8px 22px rgba(15, 23, 42, .04);
}

.historical-review-page ::v-deep .statistics .list .box-card::before {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 3px;
  content: '';
  background: #2563eb;
}

.historical-review-page ::v-deep .statistics .list:nth-child(1) .box-card::before {
  background: #10b981;
}

.historical-review-page ::v-deep .statistics .list:nth-child(2) .box-card::before {
  background: #f59e0b;
}

.historical-review-page ::v-deep .statistics .list:nth-child(3) .box-card::before {
  background: #2563eb;
}

.historical-review-page ::v-deep .statistics .list .box-card .title .label {
  color: #64748b;
  font-weight: 500;
}

.historical-review-page ::v-deep .statistics .list .box-card .value {
  margin: 14px 0 10px;
}

.historical-review-page ::v-deep .statistics .list .box-card .value p:nth-child(1) {
  color: #172033;
  font-size: 28px;
  line-height: 36px;
}

.historical-review-page ::v-deep .statistics .list .box-card .value p:nth-child(2) {
  color: #64748b;
  font-size: 13px;
}

.panel-card {
  ::v-deep .el-card__body {
    padding: 0;
  }
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 52px;
  padding: 0 18px;
  background: #fbfdff;
  border-bottom: 1px solid #e3eaf4;
}

.panel-title {
  position: relative;
  padding-left: 10px;
  color: #172033;
  font-size: 16px;
  font-weight: 700;

  &::before {
    position: absolute;
    top: 4px;
    left: 0;
    width: 3px;
    height: 14px;
    content: '';
    background: #2563eb;
    border-radius: 2px;
  }
}

.panel-hint {
  padding: 3px 10px;
  color: #64748b;
  font-size: 12px;
  background: #eef4ff;
  border: 1px solid #dbeafe;
  border-radius: 999px;
}

.chart {
  width: 100%;
  height: 330px;
  background: #fff;
}

.summary-table {
  width: 100%;

  ::v-deep .el-table__header th {
    font-weight: 700;
  }

  ::v-deep .el-table__body td {
    color: #334155;
  }

  ::v-deep .el-table__row:hover > td {
    background: #f8fbff;
  }
}

.detail-card {
  ::v-deep .avue-crud {
    padding: 12px 16px 16px;
  }

  ::v-deep .avue-crud__menu,
  ::v-deep .avue-crud__search {
    display: none;
  }

  ::v-deep .el-table {
    color: #334155;
  }

  ::v-deep .el-table th {
    color: #334155;
    font-weight: 700;
    background: #f3f6fb;
  }

  ::v-deep .el-table td,
  ::v-deep .el-table th.is-leaf {
    border-bottom-color: #e8edf5;
  }

  ::v-deep .el-table--striped .el-table__body tr.el-table__row--striped td {
    background: #fbfdff;
  }

  ::v-deep .el-pagination {
    margin-top: 12px;
  }
}
</style>
